<template>
  <div class="ts-subnav">
    <div class="ts-subnav-bar">
      <div class="container">
        <div class="ts-subnav-row">
          <nav class="ts-subnav-links" :aria-label="portalName || 'Talent Space'">
            <router-link
              v-for="item in visibleNavigation"
              :key="item.url"
              class="ts-subnav-link text-nowrap"
              :class="{ active: isActive(item.url) }"
              :aria-current="isActive(item.url) ? 'page' : null"
              :to="{ path: item.url, query: { lang } }"
              @click.native="handleOpenTab($route.name, isActive(item.url))"
            >
              {{ item.text }}
            </router-link>
          </nav>
          <!-- Same actions twice: icon-only on phones, with labels from md up (as in the design) -->
          <div
            v-for="compact in [true, false]"
            :key="`actions-${compact}`"
            :class="compact ? 'd-md-none flex-shrink-0' : 'd-none d-md-flex align-items-center'"
          >
            <div class="ts-subnav-actions d-flex align-items-center" :class="{ 'ts-subnav-actions--compact': compact }">
              <button
                v-if="isAuth"
                v-b-toggle.auth-sidebar
                class="btn btn-outline-secondary ts-btn-sm text-nowrap"
                type="button"
                :aria-label="userName || translations[13716]"
                :title="compact ? userName : null"
              >
                <i class="bi bi-person-circle" :class="{ 'me-1': !compact }" aria-hidden="true"></i>
                <span v-if="!compact && userName">{{ userName }}</span>
              </button>
              <button
                v-else
                v-b-modal.login-modal
                class="btn btn-outline-secondary ts-btn-sm text-nowrap"
                type="button"
                :aria-label="translations[13716]"
                :title="compact ? translations[13716] : null"
                @click="authEvent"
              >
                <i class="bi bi-box-arrow-in-right" :class="{ 'me-1': !compact }" aria-hidden="true"></i>
                <span v-if="!compact">{{ translations[13716] }}</span>
              </button>
              <button
                v-if="isAuth || isCurses || isTalentspace"
                v-b-toggle.notebook-sidebar
                class="btn btn-outline-secondary ts-btn-sm ts-icon-button"
                type="button"
                :aria-label="translations[13795]"
                :title="translations[13795]"
              >
                <i class="bi bi-bookmark" aria-hidden="true"></i>
                <span v-if="notebookLength" class="badge rounded-pill ts-nav-notebook-count">{{ notebookLength }}</span>
              </button>
              <language-header-themes />
            </div>
          </div>
        </div>
      </div>
    </div>
    <LoginModal />
    <RegisterModal />
    <ResetPasswordModal modal-id="applicant-reset-password" user-type="applicant" />
    <ResetPasswordModal modal-id="company-reset-password" user-type="company" />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { AppModule, AuthTab, SettingsCounters } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import LoginModal from '@/components/Authorization/LoginModal.vue';
import RegisterModal from '@/components/Authorization/RegisterModal.vue';
import ResetPasswordModal from '@/components/Authorization/ResetPasswordModal.vue';
import LanguageHeaderThemes from '@/components/Layout/LanguageHeaderThemes.vue';
import { NotebookModule } from '@/store/modules/notebook';
import { WorkersModule } from '@/store/modules/workers';
import { ProfessionModule } from '@/store/modules/profession';
import { ClustersModule } from '@/store/modules/clusters';
import { CompanyModule } from '@/store/modules/company';
import { group, groupCurses } from '@/config/configuration';
import { isTalentspaceSite } from '@/sites';

@Component({
  components: {
    LoginModal,
    RegisterModal,
    LanguageHeaderThemes,
    ResetPasswordModal,
  },
})
export default class PagesNavigation extends Vue {
  get translations() {
    return AppModule.translation;
  }

  get portalName() {
    return AppModule.settingPortal.portal_name;
  }

  get isAuth() {
    return UserModule.isUserAuth;
  }

  // TokenApplicant
  get accessTokenApplicant() {
    return UserModule.accessTokenApplicant;
  }

  // TokenConsultant
  get accessTokenConsultant() {
    return UserModule.accessTokenConsultant;
  }

  // TokenCompany
  get accessTokenCompany() {
    return UserModule.accessTokenCompany;
  }

  get userInfoApplicant() {
    return UserModule.userInfoApplicant;
  }

  get userInfoConsultant() {
    return UserModule.userInfoConsultant;
  }

  get userInfoCompany() {
    return UserModule.userInfoCompany;
  }

  get notebookLength() {
    return Number(NotebookModule.notebookJobs.length) + Number(NotebookModule.notebookWorkers.length) + Number(NotebookModule.notebookCompany.length) + Number(NotebookModule.notebookCourses.length);
  }

  get sid() {
    return AppModule.sid;
  }

  get isHide() {
    return !group(this.sid);
  }

  get getCounters() {
    return AppModule.settingPortal.counters;
  }

  isShowPage(value: SettingsCounters['name']) {
    return this.getCounters.find((val) => val.name === value)?.visible;
  }

  get navigation() {
    return [
      {
        url: '/',
        text: this.translations[13801],
        visible: true,
      },
      {
        url: '/jobs',
        text: this.isCurses ? this.translations[15398] : this.translations[12604],
        visible: this.isShowPage('jobs'),
      },
      {
        url: '/workers',
        text: this.isCurses ? this.translations[15399] : this.translations[4741],
        visible: this.isShowPage('applicants'),
      },
      {
        url: '/courses',
        text: this.translations[15391],
        visible: this.isShowPage('courses'),
      },
    ];
  }

  get isCurses() {
    return groupCurses(this.sid);
  }

  // The Talentspace design always shows the notebook button; guests keep their notebook in the browser
  get isTalentspace() {
    return isTalentspaceSite(this.sid);
  }

  get visibleNavigation() {
    return this.navigation.filter((item) => item.visible);
  }

  get lang() {
    return AppModule.language;
  }

  // Sub-pages (e.g. /jobs/catalog) keep their main item active
  isActive(url: string) {
    if (url === '/') return this.$route.path === '/';
    return this.$route.path === url || this.$route.path.startsWith(`${url}/`);
  }

  get userName() {
    if (this.accessTokenApplicant && this.userInfoApplicant?.applicant.firstname) {
      const { firstname, lastname } = this.userInfoApplicant.applicant;
      return `${firstname} ${lastname || ''}`.trim();
    }
    if (this.accessTokenConsultant && this.userInfoConsultant) {
      const { firstname, lastname } = this.userInfoConsultant.consultant;
      return `${firstname} ${lastname || ''}`.trim();
    }
    if (this.accessTokenCompany && this.userInfoCompany) {
      return this.userInfoCompany.company?.company_name || '';
    }
    return '';
  }

  authEvent() {
    UserModule.IS_REDIRECT_TO_CABINET(true);
  }

  handleOpenTab(currentRoute: string, isActive: boolean) {
    // reset every tab here;
    if (isActive) {
      // Every page of a section (Jobs, JobsResults, JobsCatalog …) resets as one
      const sectionRoutes: Record<string, string> = { jobs: 'Jobs', workers: 'Workers' };
      const tab = sectionRoutes[this.$route.meta?.section] || currentRoute;
      switch (tab) {
        case 'Jobs':
          AppModule.SetVisibleJobs(false);
          AppModule.SetVisibleMap(false);
          AppModule.SetVisibleClusterJobs(false);
          ProfessionModule.SetParamsDefault();
          ProfessionModule.SetTagsDefault();
          ClustersModule.ResetClusterData();
          break;
        case 'Workers':
          WorkersModule.resetAllWorkersFilters();
          break;
        case 'Company':
          CompanyModule.SetParamsDefaultCompany();
          CompanyModule.SET_SELECTED_COMPANIES([]);
          AppModule.SetVisibleCompany(false);
          break;
        default:
          break;
      }
    }
  }

  handleAuthQuery() {
    const query = this.$route.query.auth;
    let selectedAuthTab = AuthTab.applicant;
    let setRegistrationTab = false;

    switch (query) {
      case 'a-login':
        break;
      case 'f-login':
        selectedAuthTab = AuthTab.firm;
        break;
      case 'c-login':
        selectedAuthTab = AuthTab.support;
        break;
      case 'a-registration':
        setRegistrationTab = true;
        break;
      case 'f-registration':
        selectedAuthTab = AuthTab.firm;
        setRegistrationTab = true;
        break;
      default:
        return;
    }

    AppModule.SET_SELECTED_AUTH_TAB(selectedAuthTab);

    if (setRegistrationTab) {
      this.$bvModal.show('register-modal');
      return;
    }

    this.$bvModal.show('login-modal');
  }

  // TODO: maybe move to app? but no need with iframe
  mounted() {
    if (!this.accessTokenApplicant && !this.accessTokenCompany && !this.accessTokenConsultant) {
      this.handleAuthQuery();
    }
  }
}
</script>
